import { BaseLayout } from '@web/features/layouts/base';

/**
 * # TermsandCondtionsPage
 * @packageDescription
 *
 * Skeleton Terms and Condtions page - more to come
 */
export function TermsandCondtionsPage() {
  return (
    <BaseLayout withBackButton>
      <div
        style={{
          maxWidth: '35em',
          backgroundColor: 'rgba(0, 0, 0, .5)',
          padding: '1em',
          margin: 'auto',
        }}
      >
        <h1>Applied Love Labs Terms and Conditions</h1>
        <p>Last updated: August 1, 2024</p>
        <p>
          Please read these terms and conditions carefully before using Our
          Service.
        </p>
        <p>
          In keeping with the sentiment of the Data Privacy Policy, we want you
          to know we respect you and care about your experience. In order for
          people to feel this same respect, we have a few boundaries for how
          Time Machine is used:
        </p>
        <ol>
          <li>
            When you use Time Machine, you agree to follow our community rules.
            If you're under 18, make sure you have an adult with you.
          </li>
          <li>
            You can create an account to access all the cool stuff we offer, but
            keep your password safe!
          </li>
          <li>You can post content, but make sure it's nice and respectful.</li>
          <li>
            We respect your privacy and work hard to protect your personal
            information.&nbsp; See our Applied Love Labs privacy policy{' '}
            <a href="https://app.timemachine.love/help/privacy-policy">here</a>.
          </li>
          <li>
            If you have any questions or need help, just email us at
            help@applied.love.
          </li>
        </ol>
        <p>
          That is the gist. Like our Privacy Policy we will do our best to keep
          explanations short, use plain language, put information in intuitive
          sections, and keep legalistic explanations to a minimum.
        </p>
        <p>For more specifics, please see the full terms of use below.</p>
        <p>We hope you enjoy Time Machine!</p>
        <p>With love,</p>
        <p>The Applied Love Labs Team&nbsp;</p>
        <h2>Interpretation and Definitions</h2>
        <h3>Interpretation:</h3>
        <p>
          Words in the <em>Terms and Conditions</em> and the{' '}
          <em>Data Privacy Policy</em> having its initial letter capitalized
          have specific meanings defined under the Applied Love Labs{' '}
          <em>Terms and Conditions</em>. The following definitions shall have
          the same meaning whether they appear in the singular or in the plural.
        </p>
        <h3>Definitions:</h3>
        <p>
          <em>Account</em> or <em>User Account</em> means a unique account
          created for You, the User, to access Applied Love Labs Services or parts of Applied Love Labs
          Services.
        </p>
        <p>
          <em>Country</em> refers to: The United States of America.
        </p>
        <p>
          <em>Company</em> (referred to as either "the Company", "We", "Us" or
          "Our" in this Agreement) refers to Applied Love Labs.
        </p>
        <p>
          <em>Content</em> refers to content such as text, images, or other
          information that can be posted, uploaded, linked to or otherwise made
          available by You, regardless of the form of that Content.
        </p>
        <p>
          <em>Device</em> means any device that can access the Services such as
          a computer, a cell phone or a digital tablet.
        </p>
        <p>
          <em>Feedback</em> means feedback, innovations or suggestions sent by
          You regarding the attributes, performance or features of Applied Love Labs
          Services.
        </p>
        <p>
          <em>Service</em> or <em>Services</em> refers to the tools Applied Love Labs
          utilizes (e.g. the newsletter or the Time Machine app) through its
          applications and website to support its Users.
        </p>
        <p>
          <em>Terms and Conditions</em> (also referred as "<em>Terms</em>") mean
          these Terms and Conditions that form the entire agreement between You
          and the Company regarding the use of Applied Love Labs Services.
        </p>
        <p>
          <em>Third-party Social Media Service</em> means any services or
          content (including data, information, products or services) provided
          by a third-party that may be displayed, included or made available by
          the Service.
        </p>
        <p>
          <em>Website</em> refers to the Applied Love Labs website at:
          https://applied.love
        </p>
        <p>
          <em>Time Machine Website</em> refers to the TM Website at
          http://www.timemachine.love
        </p>
        <p>
          <em>You</em> or <em>the User</em> means the individual accessing or
          using the Service; or the company, or other legal entity on behalf of
          which such individual is accessing or using the Service, as
          applicable.
        </p>
        <h2>
          <strong>Acknowledgment</strong>
        </h2>
        <p>
          These are the Terms and Conditions governing the use of Applied Love Labs and Time
          Machine Services. The agreement operates and governs the relationship
          between You and the Company. These Terms and Conditions set out the
          rights and obligations of all users regarding the use of Applied Love Labs
          Services.
        </p>
        <p>
          Your access to and use of Applied Love Labs Services is conditioned on Your
          acceptance of and compliance with these Terms and Conditions. These
          Terms and Conditions apply to all visitors, users and others who
          access or use Applied Love Labs Services.
        </p>
        <p>
          By accessing or using the Services You, the User, agree to be bound by
          these Terms and Conditions. If You disagree with any part of these
          Terms and Conditions then You may not access Applied Love Labs Services.
        </p>
        <p>
          You represent that you are over the age of 18, or are part of a
          monitored school or youth group program that has cleared
          timemachine.love as an approved website. The Company does not permit
          those under 18 to use the Service who are not part of a monitored
          program.
        </p>
        <p>
          Your access to and use of the Service is also conditioned on Your
          acceptance of and compliance with the Data Privacy Policy of the
          Company. Our Data Privacy Policy describes Applied Love Labs policies and
          procedures on the collection, use and disclosure of Your personal
          information when You use the Application or the Website and tells You
          about Your privacy rights and how the law protects You. Please read
          Our Data Privacy Policy carefully before using Our Service.
        </p>
        <p>
          <strong>User Accounts</strong>
        </p>
        <p>
          When You create an account with Applied Love Labs, You must provide Us information
          that is accurate, complete, and current at all times. Failure to do so
          constitutes a breach of the Terms, which may result in immediate
          termination of Your account on Our Service.
        </p>
        <p>
          You are responsible for safeguarding the password that You use to
          access the Service and for any activities or actions under Your
          password, whether Your password is with Our Service or a Third-Party
          Social Media Service.
        </p>
        <p>
          You agree not to disclose Your password to any third party. You must
          notify Us immediately upon becoming aware of any breach of security or
          unauthorized use of Your account.
        </p>
        <p>
          You may not use as a username the name of another person or entity or
          that is not lawfully available for use, a name or trademark that is
          subject to any rights of another person or entity other than You
          without appropriate authorization, or a name that is otherwise
          offensive, vulgar or obscene.
        </p>
        <h2>Content</h2>
        <p>Your Right to Post Content</p>
        <p>
          Our Service allows You to post Content. You are responsible for the
          Content that You post to the Service, including its legality,
          reliability, and appropriateness.
        </p>
        <p>
          Applied Love Labs tools can be used in a manner in which your Content will be
          recycled for your own benefit and, possibly, for the benefit of the
          larger community. If at any point your Content is used it will only be
          with your prior consent.
        </p>
        <p>
          Keeping this in mind, by posting Content to the Service, with your
          consent, You grant Applied Love Labs the right and license to use, modify, publicly
          perform, publicly display, reproduce, and distribute such Content on
          and through the Service. You retain any and all of Your rights to any
          Content You submit, post or display on or through the Service and You
          are responsible for protecting those rights. You agree that this
          license includes the right for Applied Love Labs to make Your Content available to
          other users of the Service, who may also use Your Content subject to
          these Terms.
        </p>
        <p>
          You represent and warrant that: (i) the Content is Yours (You own it)
          or You have the right to use it and grant Applied Love Labs the rights and license
          as provided in these Terms, and (ii) the posting of Your Content on or
          through the Service does not violate the privacy rights, publicity
          rights, copyrights, contract rights or any other rights of any person.
        </p>
        <h2>Content Restrictions</h2>
        <p>
          The Company is not responsible for the Content of the Service's Users.
          You expressly understand and agree that You are solely responsible for
          the Content and for all activity that occurs under your account,
          whether done so by You or any third person using Your account.
        </p>
        <p>
          You may not transmit any Content that is unlawful, offensive,
          upsetting, intended to disgust, threatening, libelous, defamatory,
          obscene or otherwise objectionable. Examples of such objectionable
          Content include, but are not limited to, the following:
        </p>
        <h3>Unlawful or Promoting Unlawful Activity</h3>
        <p>
          Defamatory, discriminatory, or mean-spirited content, including
          references or commentary about religion, race, sexual orientation,
          gender, national/ethnic origin, or other targeted groups.
        </p>
        <p>
          Spam, machine &ndash; or randomly &ndash; generated, constituting
          unauthorized or unsolicited advertising, chain letters, any other form
          of unauthorized solicitation, or any form of lottery or gambling.
        </p>
        <p>
          Containing or installing any viruses, worms, malware, trojan horses,
          or other content that is designed or intended to disrupt, damage, or
          limit the functioning of any software, hardware or telecommunications
          equipment or to damage or obtain unauthorized access to any data or
          other information of a third person.
        </p>
        <p>
          Infringing on any proprietary rights of any party, including patent,
          trademark, trade secret, copyright, right of publicity or other
          rights.
        </p>
        <p>
          Impersonating any person or entity including the Company and its
          employees or representatives.
        </p>
        <p>Violating the privacy of any third person.</p>
        <h2>False information and features</h2>
        <p>
          Applied Love Labs reserves the right, but not the obligation, in its sole
          discretion, to determine whether or not any Content is appropriate and
          complies with Applied Love Labs Terms and to refuse or remove this Content. Applied Love Labs
          further reserves the right to make formatting, edits and change the
          manner in which any Content is presented. Applied Love Labs can also limit or
          revoke the use of the Service if You post such objectionable Content.
          As the Company cannot control all content posted by users and/or third
          parties on the Service, you agree to use the Service at your own risk.
          You understand that by using the Service You may be exposed to content
          that You may find offensive, indecent, incorrect or objectionable, and
          You agree that under no circumstances will the Company be liable in
          any way for any content, including any errors or omissions in any
          content, or any loss or damage of any kind incurred as a result of
          your use of any content.
        </p>
        <h2>
          <strong>Content Backups</strong>
        </h2>
        <p>
          Although regular backups of Content are performed, the Company does
          not guarantee there will be no loss or corruption of data.
        </p>
        <p>
          Corrupt or invalid backup points may be caused by, without limitation,
          Content that is corrupted prior to being backed up or that changes
          during the time a backup is performed.
        </p>
        <p>
          The Company will provide support and attempt to troubleshoot any known
          or discovered issues that may affect the backups of Content. But You
          acknowledge that the Company has no liability related to the integrity
          of Content or the failure to successfully restore Content to a usable
          state.
        </p>
        <p>
          You agree to maintain a complete and accurate copy of any Content in a
          location independent of the Service.
        </p>
        <h2>
          <strong>Copyright Policy</strong>
        </h2>
        <h3>Intellectual Property Infringement</h3>
        <p>
          At Applied Love Labs, we respect the intellectual property rights of others. It is
          Our policy to respond to any claim that Content posted on the Service
          infringes a copyright or other intellectual property infringement of
          any person.
        </p>
        <p>
          If You are a copyright owner, or authorized on behalf of one, and You
          believe that copyrighted work has been copied in a way that
          constitutes copyright infringement that is taking place through the
          Service, You must submit Your notice in writing to the attention of
          our Copyright Agent via email at{' '}
          <a href="mailto:help@applied.love">help@applied.love</a> and
          include in Your notice a detailed description of the alleged
          infringement.
        </p>
        <p>
          You will be held accountable for damages (including costs and
          attorneys' fees) for misrepresenting that any Content is infringing
          Your copyright.
        </p>
        <h3>
          DMCA Notice and DMCA Procedure for Copyright Infringement Claims
        </h3>
        <p>
          You may submit a notification pursuant to the Digital Millennium
          Copyright Act (DMCA) by providing our Copyright Agent with the
          following information in writing (see 17 U.S.C 512(c)(3) for further
          detail):
        </p>
        <p>
          An electronic or physical signature of the person authorized to act on
          behalf of the owner of the copyright's interest.
        </p>
        <p>
          A description of the copyrighted work that You claim has been
          infringed, including the URL (i.e., web page address) of the location
          where the copyrighted work exists or a copy of the copyrighted work.
        </p>
        <p>
          Identification of the URL or other specific location on the Service
          where the material that You claim is infringing is located.
        </p>
        <p>Your address, telephone number, and email address.</p>
        <p>
          A statement by You that You have a good faith belief that the disputed
          use is not authorized by the copyright owner, its agent, or the law.
        </p>
        <p>
          A statement by You, made under penalty of perjury, that the
          information in Your notice is accurate and that You are the copyright
          owner or authorized to act on the copyright owner's behalf.
        </p>
        <p>
          You can contact our copyright agent via email at{' '}
          <a href="mailto:help@applied.love">help@applied.love</a>. Upon
          receipt of a notification, the Company will take whatever action, in
          its sole discretion, it deems appropriate, including removal of the
          challenged content from the Service.
        </p>
        <h2>Your Feedback to Us</h2>
        <p>
          You assign all rights, title and interest in any Feedback You provide
          the Company. If for any reason such assignment is ineffective, You
          agree to grant the Company a non-exclusive, perpetual, irrevocable,
          royalty free, worldwide right and license to use, reproduce, disclose,
          sub-license, distribute, modify and exploit such Feedback without
          restriction.
        </p>
        <h2>
          <strong>Links to Other Websites</strong>
        </h2>
        <p>
          As detailed in our Data Privacy Policy, our Services contain links to
          third-party web sites or services that are not owned or controlled by
          the Company.
        </p>
        <p>
          The Company has no control over, and assumes no responsibility for the
          content, privacy policies, or practices of any third-party web sites
          or services. You further acknowledge and agree that the Company shall
          not be responsible or liable, directly or indirectly, for any damage
          or loss caused or alleged to be caused by or in connection with the
          use of or reliance on any such content, goods or services available on
          or through any such web sites or services.
        </p>
        <p>
          We strongly advise You to read the terms and conditions and privacy
          policies of any third-party web sites or services that You visit.
        </p>
        <h2>Termination</h2>
        <p>
          We may terminate or suspend Your Account immediately, without prior
          notice or liability, for any reason whatsoever, including without
          limitation if You breach these Terms and Conditions.
        </p>
        <p>
          Upon termination, Your right to use the Service will cease
          immediately. If You wish to terminate Your Account, You may simply
          discontinue using the Service or you can send a request to terminate
          your account.
        </p>
        <h2>Limitation of Liability</h2>
        <p>
          Notwithstanding any damages that You might incur, the entire liability
          of the Company and any of its suppliers under any provision of these
          Terms and Your exclusive remedy for all of the foregoing shall be
          limited to the amount actually paid by You through the Service or 100
          USD if You haven't purchased anything through the Service.
        </p>
        <p>
          To the maximum extent permitted by applicable law, in no event shall
          the Company or its suppliers be liable for any special, incidental,
          indirect, or consequential damages whatsoever (including, but not
          limited to, damages for loss of profits, loss of data or other
          information, for business interruption, for personal injury, loss of
          privacy arising out of or in any way related to the use of or
          inability to use the Service, third-party software and/or third-party
          hardware used with the Service, or otherwise in connection with any
          provision of Applied Love Labs Terms and Conditions), even if the Company or any
          supplier has been advised of the possibility of such damages and even
          if the remedy fails of its essential purpose.
        </p>
        <p>
          Some states do not allow the exclusion of implied warranties or
          limitation of liability for incidental or consequential damages, which
          means that some of the above limitations may not apply. In these
          states, each party's liability will be limited to the greatest extent
          permitted by law.
        </p>
        <h2>"AS IS" and "AS AVAILABLE"</h2>
        <p>
          Applied Love Labs Services are unique in that the application development is done
          collaboratively with our users. We are on this development journey
          together. That being the case, Applied Love Labs Services are provided to You "AS
          IS" and "AS AVAILABLE" and with all faults and defects without
          warranty of any kind. To the maximum extent permitted under applicable
          law, the Company, and its service providers, expressly disclaims all
          warranties, whether express, implied, statutory or otherwise, with
          respect to all Applied Love Labs Services, including all implied warranties of
          merchantability, fitness for a particular purpose, title and
          non-infringement, and warranties that may arise out of course of
          dealing, course of performance, usage or trade practice. Without
          limitation to the foregoing, the Company provides no warranty or
          undertaking, and makes no representation of any kind that the Service
          will meet Your requirements, achieve any intended results, be
          compatible or work with any other software, applications, systems or
          services, operate without interruption, meet any performance or
          reliability standards or be error free or that any errors or defects
          can or will be corrected.
        </p>
        <p>
          Without limiting the foregoing, neither the Company nor any of the
          company's provider makes any representation or warranty of any kind,
          express or implied: (i) as to the operation or availability of the
          Service, or the information, content, and materials or products
          included thereon; (ii) that the Service will be uninterrupted or
          error-free; (iii) as to the accuracy, reliability, or currency of any
          information or content provided through the Service; or (iv) that the
          Service, its servers, the content, or e-mails sent from or on behalf
          of the Company are free of viruses, scripts, trojan horses, worms,
          malware, timebombs or other harmful components.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain types of
          warranties or limitations on applicable statutory rights of a
          consumer, so some or all of the above exclusions and limitations may
          not apply to You. But in such a case the exclusions and limitations
          set forth in this section shall be applied to the greatest extent
          enforceable under applicable law.
        </p>
        <h2>Governing Law</h2>
        <p>
          The laws of the Country, excluding its conflicts of law rules, shall
          govern these Terms and Conditions as well as Your use of the Service.
          Your use of the Application may also be subject to other local, state,
          national, or international laws.
        </p>
        <h2>Disputes Resolution</h2>
        <p>
          If You have any concern or dispute about the Service, You agree to
          first try to resolve the dispute informally by contacting the Company.
        </p>
        <h3>For European Union (EU) Users</h3>
        <p>
          If You are a European Union consumer, you will benefit from any
          mandatory provisions of the law of the country in which you are
          resident in.
        </p>
        <h3>United States Legal Compliance</h3>
        <p>
          You represent and warrant that (i) You are not located in a country
          that is subject to the United States government embargo, or that has
          been designated by the United States government as a "terrorist
          supporting" country, and (ii) You are not listed on any United States
          government list of prohibited or restricted parties.
        </p>
        <h3>Severability and Waiver Severability</h3>
        <p>
          If any provision of these Terms is held to be unenforceable or
          invalid, such provision will be changed and interpreted to accomplish
          the objectives of such provision to the greatest extent possible under
          applicable law and the remaining provisions will continue in full
          force and effect.
        </p>
        <h3>Waiver</h3>
        <p>
          Except as provided herein, the failure to exercise a right or to
          require performance of an obligation under these Terms shall not
          affect a party's ability to exercise such right or require such
          performance at any time thereafter, nor shall the waiver of a breach
          constitute a waiver of any subsequent breach.
        </p>
        <h3>Translation Interpretation</h3>
        <p>
          These Terms and Conditions may have been translated if We have made
          them available to You on our Service. You agree that the original
          English text shall prevail in the case of a dispute.
        </p>
        <h2>Changes to These Terms and Conditions</h2>
        <p>
          We reserve the right, at Our sole discretion, to modify or replace
          these Terms at any time. If a revision is material We will make
          reasonable efforts to provide at least 30 days' notice prior to any
          new terms taking effect. What constitutes a material change will be
          determined at Our sole discretion.
        </p>
        <p>
          By continuing to access or use Applied Love Labs Services after those revisions
          become effective, You agree to be bound by the revised terms. If You
          do not agree to the new terms, in whole or in part, please stop using
          the website and the Services immediately.
        </p>
        <h2>Contact Us</h2>
        <p>
          If you have any questions about these Terms and Conditions, please
          contact us: by email: help@applied.love
        </p>
      </div>
    </BaseLayout>
  );
}

export default TermsandCondtionsPage;
